export const DEFAULT_INTERFACE_LOCALE = 'en-CA';
export const INTERFACE_LANGUAGE_STORAGE_KEY = 'silatrail.interface-locale';

export const INTERFACE_LANGUAGES = Object.freeze([
  Object.freeze({code: 'en-CA', label: 'English (Canada)', shortLabel: 'English'}),
  Object.freeze({code: 'fr-CA', label: 'Français (Canada)', shortLabel: 'Français'})
]);

const SUPPORTED_LOCALES = new Set(INTERFACE_LANGUAGES.map(({code}) => code));

const FRENCH_CANADIAN = Object.freeze({
  "Approval required": "Approbation requise",
  "Create income from bank transactions": "Créer des revenus à partir des transactions bancaires",
  "Save incoming money as review drafts. Nothing changes your income totals until you approve a draft. Refunds stay separate.": "Enregistrez les fonds reçus comme brouillons à réviser. Vos totaux de revenus ne changent qu’après l’approbation d’un brouillon. Les remboursements restent séparés.",
  "Select all eligible incoming transactions": "Sélectionner toutes les transactions entrantes admissibles",
  "Create {{count}} income drafts": "Créer {{count}} brouillons de revenus",
  "Select all reviewed income drafts": "Sélectionner tous les brouillons de revenus révisés",
  "Source or client": "Source ou client",
  "Tax collected": "Taxes perçues",
  "Description (optional)": "Description (facultative)",
  "Tax must be between zero and the income amount.": "La taxe doit être comprise entre zéro et le montant du revenu.",
  "Potential duplicate: {{reason}}": "Doublon potentiel : {{reason}}",
  "Review the existing record before confirming that this is separate income.": "Examinez le dossier existant avant de confirmer qu’il s’agit d’un revenu distinct.",
  "Existing income record": "Dossier de revenu existant",
  "Stripe invoice payment": "Paiement de facture Stripe",
  "Paid invoice": "Facture payée",
  "Save income draft": "Enregistrer le brouillon de revenu",
  "No incoming transactions are ready for income drafts in this view.": "Aucune transaction entrante n’est prête à devenir un brouillon de revenu dans cette vue.",
  "Add paid invoices to income": "Ajouter les factures payées aux revenus",
  "Review paid invoices before they affect income totals. SilaTrail preserves the invoice link and warns about possible duplicates.": "Examinez les factures payées avant qu’elles modifient les totaux de revenus. SilaTrail conserve le lien avec la facture et signale les doublons possibles.",
  "Select all paid invoices": "Sélectionner toutes les factures payées",
  "Create {{count}} review drafts": "Créer {{count}} brouillons à examiner",
  "{{count}} paid invoice drafts ready for review.": "{{count}} brouillons de factures payées sont prêts à être examinés.",
  "Invoice income draft saved.": "Le brouillon de revenu de facture a été enregistré.",
  "Draft removed. No income was created.": "Le brouillon a été supprimé. Aucun revenu n’a été créé.",
  "{{count}} paid invoices added to income.": "{{count}} factures payées ont été ajoutées aux revenus.",
  "Select reviewed drafts": "Sélectionner les brouillons examinés",
  "Approve {{count}} income records": "Approuver {{count}} revenus",
  "Income date": "Date du revenu",
  "Tax must be between zero and the invoice total.": "La taxe doit être comprise entre zéro et le total de la facture.",
  "Select draft for {{invoice}}": "Sélectionner le brouillon de {{invoice}}",
  "Potential duplicate income": "Revenu potentiellement en double",
  "An income record with the same amount and a nearby date already exists.": "Un revenu du même montant et d’une date proche existe déjà.",
  "No paid invoices are waiting for income review.": "Aucune facture payée n’attend un examen des revenus.",
  "1 income draft ready for review.": "1 brouillon de revenu est prêt à être révisé.",
  "{{count}} income drafts ready for review.": "{{count}} brouillons de revenus sont prêts à être révisés.",
  "Income draft changes saved.": "Les modifications du brouillon de revenu ont été enregistrées.",
  "Income draft removed. No income was created.": "Le brouillon de revenu a été supprimé. Aucun revenu n’a été créé.",
  "1 income record created and matched.": "1 dossier de revenu a été créé et rapproché.",
  "{{count}} income records created and matched.": "{{count}} dossiers de revenus ont été créés et rapprochés.",
  "Select income draft for {{source}}": "Sélectionner le brouillon de revenu pour {{source}}",
  "Matched to income from {{source}}": "Rapproché du revenu provenant de {{source}}",
  "Match existing income": "Rapprocher un revenu existant",
  "Choose an income match": "Choisir un rapprochement de revenu",
  "Bank transaction matched to existing income. Income totals are unchanged.": "La transaction bancaire a été rapprochée d’un revenu existant. Les totaux de revenus sont inchangés.",
  "No eligible income records found.": "Aucun revenu admissible n’a été trouvé.",
  ", then return to match it. Matching never creates duplicate income.": ", puis revenez pour le rapprocher. Le rapprochement ne crée jamais de revenu en double.",
  "Paid invoice for {{client}}": "Facture payée pour {{client}}",
  "Source name matches": "Le nom de la source correspond",
  "Source name overlaps": "Le nom de la source correspond partiellement",
  "Linked paid invoice": "Facture payée liée",
  "Create expenses from bank transactions": "Créer des dépenses à partir des transactions bancaires",
  "Save selected purchases as review drafts. Drafts do not change your accounting. After you review and approve them, SilaTrail creates missing-receipt expenses and matches the bank transactions.": "Enregistrez les achats sélectionnés comme brouillons à réviser. Les brouillons ne modifient pas votre comptabilité. Après votre révision et votre approbation, SilaTrail crée des dépenses avec reçu manquant et rapproche les transactions bancaires.",
  "Select all eligible purchases": "Sélectionner tous les achats admissibles",
  "Creating drafts…": "Création des brouillons…",
  "Create {{count}} drafts": "Créer {{count}} brouillons",
  "Select all reviewed drafts": "Sélectionner tous les brouillons révisés",
  "Approving…": "Approbation…",
  "Approve and match {{count}}": "Approuver et rapprocher {{count}}",
  "Select draft for {{merchant}}": "Sélectionner le brouillon pour {{merchant}}",
  "Receipt will be marked missing": "Le reçu sera marqué comme manquant",
  "Save draft": "Enregistrer le brouillon",
  "Remove draft": "Supprimer le brouillon",
  "No unmatched purchases are ready for drafts in this view.": "Aucun achat non rapproché n’est prêt à devenir un brouillon dans cette vue.",
  "1 draft ready for review.": "1 brouillon est prêt à être révisé.",
  "{{count}} drafts ready for review.": "{{count}} brouillons sont prêts à être révisés.",
  "Draft changes saved.": "Les modifications du brouillon ont été enregistrées.",
  "Draft removed. No expense was created.": "Le brouillon a été supprimé. Aucune dépense n’a été créée.",
  "1 expense created and matched.": "1 dépense a été créée et rapprochée.",
  "{{count}} expenses created and matched.": "{{count}} dépenses ont été créées et rapprochées.",
  "Online invoice payments": "Paiements de factures en ligne",
  "Connect Stripe for this Business to let customers pay invoices online. Stripe pays your linked bank account; processing fees apply.": "Connectez Stripe pour cette entreprise afin que les clients paient les factures en ligne. Stripe verse les fonds à votre compte bancaire associé; des frais de traitement s’appliquent.",
  "Only the Business owner can manage payment setup.": "Seul le propriétaire de l’entreprise peut gérer la configuration des paiements.",
  "Loading payment settings…": "Chargement des paramètres de paiement…",
  "Payment settings are unavailable. Manual payment recording remains available.": "Les paramètres de paiement ne sont pas disponibles. L’enregistrement manuel des paiements reste disponible.",
  "Online payments are not enabled yet. Manual payment recording remains available.": "Les paiements en ligne ne sont pas encore activés. L’enregistrement manuel des paiements reste disponible.",
  "Invoice payments enabled": "Paiements de factures activés",
  "Stripe ready · payments disabled": "Stripe prêt · paiements désactivés",
  "Stripe setup incomplete": "Configuration Stripe incomplète",
  "Stripe not connected": "Stripe non connecté",
  "Stripe test mode · no real payments": "Mode test Stripe · aucun paiement réel",
  "Review Stripe setup": "Vérifier la configuration Stripe",
  "Set up Stripe": "Configurer Stripe",
  "Disable invoice payments": "Désactiver les paiements de factures",
  "Enable invoice payments": "Activer les paiements de factures",
  "Refresh status": "Actualiser le statut",
  "Disabling payments stops new checkout requests. Existing checkout sessions can still complete. Close them from the invoice payment history when needed.": "La désactivation bloque les nouvelles demandes de paiement. Les sessions existantes peuvent encore aboutir. Fermez-les dans l’historique des paiements au besoin.",
  "Unable to update payment settings. Refresh status and try again.": "Impossible de modifier les paramètres de paiement. Actualisez le statut et réessayez.",
  "Online payment history": "Historique des paiements en ligne",
  "Stripe payments are recorded here and update invoice status. Review income separately to avoid duplicate revenue records.": "Les paiements Stripe sont enregistrés ici et mettent à jour le statut des factures. Examinez les revenus séparément pour éviter les doublons.",
  "Stripe payment received": "Paiement Stripe reçu",
  "Checkout closed": "Session de paiement fermée",
  "Online payment in progress": "Paiement en ligne en cours",
  "Stripe refunded": "Remboursé par Stripe",
  "Payment dispute · review in Stripe": "Litige de paiement · à examiner dans Stripe",
  "Close this checkout before recording a manual payment or voiding the invoice?": "Fermer cette session avant d’enregistrer un paiement manuel ou d’annuler la facture?",
  "Close checkout": "Fermer la session de paiement",
  "Unable to close checkout. It may have completed; refresh payment history before trying again.": "Impossible de fermer la session. Le paiement a peut-être abouti; actualisez l’historique avant de réessayer.",
  "Opening secure checkout…": "Ouverture du paiement sécurisé…",
  "Pay now": "Payer maintenant",
  "Invoice paid": "Facture payée",
  "Waiting for payment confirmation. Your invoice updates after Stripe confirms payment. Refresh if needed.": "En attente de confirmation. Votre facture sera mise à jour après la confirmation de Stripe. Actualisez au besoin.",
  "Unable to start payment. Refresh the invoice or contact the Business.": "Impossible de lancer le paiement. Actualisez la facture ou contactez l’entreprise.",
  'Your language preference was not saved. Please try again.': 'Votre préférence linguistique n’a pas été enregistrée. Veuillez réessayer.',
  'Overview': 'Aperçu',
  'Dashboard': 'Tableau de bord',
  'Home': 'Accueil',
  'Expenses': 'Dépenses',
  'Receipt inbox': 'Boîte de reçus',
  'Reminders': 'Rappels',
  'Accountant requests': 'Demandes du comptable',
  'Bank statements': 'Relevés bancaires',
  'Income': 'Revenus',
  'Invoices': 'Factures',
  'Repayments': 'Remboursements',
  'Trips': 'Déplacements',
  'Vehicles': 'Véhicules',
  'Contractors': 'Travailleurs autonomes',
  'Tax Centre': 'Centre fiscal',
  'Review': 'Révision',
  'Support': 'Soutien',
  'Feature requests': 'Demandes de fonctionnalités',
  'Settings': 'Paramètres',
  'Financial records': 'Dossiers financiers',
  'Business operations': 'Opérations de l’entreprise',
  'Reporting & settings': 'Rapports et paramètres',
  'Accountant workspace': 'Espace du comptable',
  'Requests': 'Demandes',
  'Monthly review': 'Révision mensuelle',
  'Profile': 'Profil',
  'Business': 'Entreprise',
  'Payment Methods': 'Modes de paiement',
  'Merchant rules': 'Règles des commerçants',
  'Accountant access': 'Accès du comptable',
  'Billing': 'Facturation',
  'Workspace': 'Espace de travail',
  'Your Business': 'Votre entreprise',
  'Your workspace': 'Votre espace de travail',
  'More': 'Plus',
  'Close menu': 'Fermer le menu',
  'Close': 'Fermer',
  'All pages': 'Toutes les pages',
  'Account': 'Compte',
  'Sign out': 'Se déconnecter',
  'Signing out…': 'Déconnexion…',
  'Install SilaTrail': 'Installer SilaTrail',
  'Switch Business': 'Changer d’entreprise',
  'Choose a Business': 'Choisir une entreprise',
  'Your Businesses': 'Vos entreprises',
  'Add a Business': 'Ajouter une entreprise',
  'Create your first Business': 'Créer votre première entreprise',
  'Unnamed Business': 'Entreprise sans nom',
  'No accessible Businesses.': 'Aucune entreprise accessible.',
  'Owner workspace': 'Espace du propriétaire',
  'Team access': 'Accès de l’équipe',
  'Secure read-only access': 'Accès sécurisé en lecture seule',
  'A Silahub Technologies company': 'Une entreprise de Silahub Technologies',
  'Skip to content': 'Passer au contenu',
  'Settings navigation': 'Navigation des paramètres',
  'Main navigation': 'Navigation principale',
  'Mobile navigation': 'Navigation mobile',
  'Account settings': 'Paramètres du compte',
  'Manage your account details and SilaTrail preferences.': 'Gérez les détails de votre compte et vos préférences SilaTrail.',
  'Manage your account information and preferences.': 'Gérez les renseignements et les préférences de votre compte.',
  'Full name': 'Nom complet',
  'Your full name': 'Votre nom complet',
  'Email address': 'Adresse courriel',
  'To change your email, please contact support.': 'Pour modifier votre adresse courriel, communiquez avec le soutien.',
  'Interface language': 'Langue de l’interface',
  'Choose the language used for navigation and supported screens. Business country, currency and tax settings do not change.': 'Choisissez la langue de la navigation et des écrans pris en charge. Le pays, la devise et les paramètres fiscaux de l’entreprise ne changent pas.',
  'Save changes': 'Enregistrer les modifications',
  'Saving…': 'Enregistrement…',
  'All changes saved': 'Toutes les modifications sont enregistrées',
  'English (Canada)': 'English (Canada)',
  'Français (Canada)': 'Français (Canada)',
  'Trips & mileage': 'Déplacements et kilométrage',
  'Business travel for {{business}} · This month': 'Déplacements professionnels de {{business}} · Ce mois-ci',
  'Record trip': 'Enregistrer un déplacement',
  'Trips this month': 'Déplacements ce mois-ci',
  'Business distance': 'Distance professionnelle',
  'Estimated fuel cost': 'Coût estimatif du carburant',
  'Business portion · {{included}} of {{count}} trips estimated': 'Part professionnelle · {{included}} déplacements estimés sur {{count}}',
  'Fuel price ({{currency}}/L)': 'Prix du carburant ({{currency}}/L)',
  'Fuel price ({{currency}}/{{unit}})': 'Prix du carburant ({{currency}}/{{unit}})',
  'Enter price': 'Saisir le prix',
  'Recorded distance: {{distance}} km. Price is used for this estimate only.': 'Distance enregistrée : {{distance}} km. Le prix sert uniquement à cette estimation.',
  'Recorded distance: {{distance}}. Price is used for this estimate only.': 'Distance enregistrée : {{distance}}. Le prix sert uniquement à cette estimation.',
  'Vehicle': 'Véhicule',
  'All vehicles': 'Tous les véhicules',
  'Loading trips…': 'Chargement des déplacements…',
  'Unable to load trips. Please try again.': 'Impossible de charger les déplacements. Veuillez réessayer.',
  'No trips recorded this month': 'Aucun déplacement enregistré ce mois-ci',
  'Log your business trips to keep your mileage records complete.': 'Consignez vos déplacements professionnels pour garder vos registres de kilométrage complets.',
  'Start not entered': 'Départ non saisi',
  'Destination not entered': 'Destination non saisie',
  'No vehicle selected': 'Aucun véhicule sélectionné',
  'Route estimate': 'Estimation de l’itinéraire',
  'Odometer': 'Odomètre',
  'Manual distance': 'Distance manuelle',
  'Choose a Business before viewing trips.': 'Choisissez une entreprise avant de consulter les déplacements.',
  'Manage vehicles used by {{business}}.': 'Gérez les véhicules utilisés par {{business}}.',
  'Add vehicle': 'Ajouter un véhicule',
  'Loading vehicles…': 'Chargement des véhicules…',
  'Add your first vehicle': 'Ajoutez votre premier véhicule',
  'Add a vehicle to record odometer-based business trips.': 'Ajoutez un véhicule pour enregistrer les déplacements professionnels selon l’odomètre.',
  'Vehicle details not provided': 'Détails du véhicule non fournis',
  'Current odometer': 'Odomètre actuel',
  'Archive {{vehicle}}': 'Archiver {{vehicle}}',
  'Choose a Business before managing vehicles.': 'Choisissez une entreprise avant de gérer les véhicules.',
  'Revenue · {{business}}': 'Revenus · {{business}}',
  'Track revenue and tax collected for your Business.': 'Suivez les revenus et les taxes perçues de votre entreprise.',
  'Add income': 'Ajouter un revenu',
  'Month': 'Mois',
  'Year': 'Année',
  'Income this month': 'Revenus ce mois-ci',
  'Loading income…': 'Chargement des revenus…',
  'Unable to load income records. Please try again.': 'Impossible de charger les revenus. Veuillez réessayer.',
  'No income was recorded for this month.': 'Aucun revenu n’a été enregistré ce mois-ci.',
  'Tax: {{amount}}': 'Taxe : {{amount}}',
  'Choose a Business before viewing income.': 'Choisissez une entreprise avant de consulter les revenus.',
  'Business settings': 'Paramètres de l’entreprise',
  'Manage details for {{business}}.': 'Gérez les détails de {{business}}.',
  'General': 'Général',
  'Tax & Compliance': 'Fiscalité et conformité',
  'Business name': 'Nom de l’entreprise',
  'Legal structure': 'Structure juridique',
  'Sole Proprietorship': 'Entreprise individuelle',
  'Partnership': 'Société de personnes',
  'Corporation': 'Société par actions',
  'Non-Profit': 'Organisme sans but lucratif',
  'Country': 'Pays',
  'Language': 'Langue',
  'Currency': 'Devise',
  'Not configured': 'Non configuré',
  'Description': 'Description',
  'Fiscal year end': 'Fin de l’exercice',
  'GST/HST number': 'Numéro de TPS/TVH',
  'Optional registration number': 'Numéro d’inscription facultatif',
  'Business settings saved.': 'Les paramètres de l’entreprise sont enregistrés.',
  'Create a Business before editing settings.': 'Créez une entreprise avant de modifier les paramètres.',
  'United States preparation organizes federal records and points you to state/local obligations. SilaTrail does not calculate tax or determine filing requirements.': 'La préparation pour les États-Unis organise les dossiers fédéraux et vous oriente vers les obligations étatiques et locales. SilaTrail ne calcule pas l’impôt et ne détermine pas les exigences de déclaration.',
  'Canadian tax preparation uses GST/HST. Currency is fixed to CAD for Canadian financial records.': 'La préparation fiscale canadienne utilise la TPS/TVH. La devise est fixée au CAD pour les dossiers financiers canadiens.',
  'Tax preparation is not available for this Business country. Existing records remain accessible, but Canadian GST/HST guidance is hidden to prevent incorrect use.': 'La préparation fiscale n’est pas offerte pour le pays de cette entreprise. Les dossiers existants demeurent accessibles, mais les conseils canadiens sur la TPS/TVH sont masqués afin d’éviter une utilisation incorrecte.',
  'Records': 'Dossiers',
  'Review expenses for {{business}}.': 'Examinez les dépenses de {{business}}.',
  'your Business': 'votre entreprise',
  'Add expense': 'Ajouter une dépense',
  'Expense updated successfully.': 'La dépense a été mise à jour.',
  'Choose a Business before reviewing expenses.': 'Choisissez une entreprise avant d’examiner les dépenses.',
  'Expense records': 'Dossiers de dépenses',
  'One record per expense, with receipts kept private.': 'Un dossier par dépense, avec les reçus conservés en privé.',
  '{{count}} records': '{{count}} dossiers',
  'Unable to load expenses. Please try again.': 'Impossible de charger les dépenses. Veuillez réessayer.',
  'No expenses recorded yet': 'Aucune dépense enregistrée',
  'Capture your first Business expense and keep your records ready for review.': 'Saisissez votre première dépense d’entreprise et gardez vos dossiers prêts pour l’examen.',
  'Expense': 'Dépense',
  'Date': 'Date',
  'Paid with': 'Payé avec',
  'Category': 'Catégorie',
  'Merchant category rules': 'Règles de catégorie par commerçant',
  'Bank automation': 'Automatisation bancaire',
  'Bank transaction rules': 'Règles de transactions bancaires',
  'Suggest a review action when imported transaction descriptions match. Rules never apply until you confirm them in Bank statements.': 'Suggérez une action de révision lorsque les descriptions des transactions importées correspondent. Les règles ne s’appliquent jamais avant votre confirmation dans les relevés bancaires.',
  'Add a bank rule': 'Ajouter une règle bancaire',
  'Transaction description': 'Description de la transaction',
  'e.g. Shell or Adobe': 'p. ex. Shell ou Adobe',
  'Match': 'Correspondance',
  'Contains': 'Contient',
  'Exact transaction description': 'Description exacte de la transaction',
  'Suggested action': 'Action suggérée',
  'Create expense draft': 'Créer un brouillon de dépense',
  'Draft expense': 'Dépense en brouillon',
  'Money direction': 'Sens des fonds',
  'Money spent or received': 'Fonds dépensés ou reçus',
  'Money spent': 'Sommes dépensées',
  'Money received': 'Sommes reçues',
  'Any direction': 'Tous les sens',
  'Direction is fixed for this action.': 'Le sens est fixe pour cette action.',
  'Use a specific direction when the same text can appear on credits and debits.': 'Utilisez un sens précis lorsque le même texte peut figurer parmi les crédits et les débits.',
  'This rule creates a suggestion only. You must confirm it from Bank statements.': 'Cette règle crée uniquement une suggestion. Vous devez la confirmer dans les relevés bancaires.',
  'Save bank rule': 'Enregistrer la règle bancaire',
  'Bank rule saved.': 'La règle bancaire est enregistrée.',
  'Saved bank rules': 'Règles bancaires enregistrées',
  'Exact and direction-specific rules take priority over broader matches.': 'Les règles exactes et propres à un sens ont priorité sur les correspondances plus larges.',
  '{{count}} rules': '{{count}} règles',
  'Loading bank rules…': 'Chargement des règles bancaires…',
  'Unable to load bank rules.': 'Impossible de charger les règles bancaires.',
  'No bank rules yet': 'Aucune règle bancaire',
  'Add a rule for transaction descriptions you review repeatedly.': 'Ajoutez une règle pour les descriptions de transactions que vous révisez souvent.',
  'Paused': 'En pause',
  'Pause': 'Mettre en pause',
  'Resume': 'Reprendre',
  'Delete this bank rule?': 'Supprimer cette règle bancaire?',
  'Review before applying': 'Réviser avant d’appliquer',
  'Bank rule suggestions': 'Suggestions des règles bancaires',
  'Rules organize matching transactions only after you confirm them. Expense rules create review drafts; they do not post expenses.': 'Les règles organisent les transactions correspondantes seulement après votre confirmation. Les règles de dépenses créent des brouillons à réviser; elles ne comptabilisent pas les dépenses.',
  'Applying…': 'Application…',
  'Apply {{count}} selected': 'Appliquer les {{count}} sélections',
  'Checking bank rules…': 'Vérification des règles bancaires…',
  'Unable to check bank rules. Refresh and try again.': 'Impossible de vérifier les règles bancaires. Actualisez la page et réessayez.',
  'Select all rule suggestions': 'Sélectionner toutes les suggestions de règles',
  'Exact description rule': 'Règle de description exacte',
  'Contains description rule': 'Règle de description partielle',
  '1 bank rule applied.': '1 règle bancaire appliquée.',
  '{{count}} bank rules applied.': '{{count}} règles bancaires appliquées.',
  'Suggest the category you usually choose for a merchant. You still review and confirm every expense before saving.': 'Suggérez la catégorie que vous choisissez habituellement pour un commerçant. Vous révisez et confirmez toujours chaque dépense avant de l’enregistrer.',
  'Add a rule': 'Ajouter une règle',
  'Merchant text': 'Texte du commerçant',
  'e.g. Adobe': 'p. ex. Adobe',
  'Exact merchant name': 'Nom exact du commerçant',
  'Suggested category': 'Catégorie suggérée',
  'Save rule': 'Enregistrer la règle',
  'Rule saved.': 'Règle enregistrée.',
  'Saved rules': 'Règles enregistrées',
  'Exact matches take priority; longer contains matches win when more than one rule applies.': 'Les correspondances exactes ont priorité; le texte le plus long l’emporte lorsque plusieurs règles s’appliquent.',
  'Loading rules…': 'Chargement des règles…',
  'Unable to load merchant rules.': 'Impossible de charger les règles des commerçants.',
  'No merchant rules yet': 'Aucune règle de commerçant',
  'Add one for a merchant you categorize often. Nothing is changed automatically.': 'Ajoutez-en une pour un commerçant que vous catégorisez souvent. Rien n’est modifié automatiquement.',
  'Delete this merchant rule?': 'Supprimer cette règle de commerçant?',
  'Choose a Business before managing rules.': 'Choisissez une entreprise avant de gérer les règles.',
  '{{category}} is suggested by your “{{merchant}}” rule.': 'La catégorie {{category}} est suggérée par votre règle « {{merchant}} ».',
  'Use suggested category': 'Utiliser la catégorie suggérée',
  'Amount': 'Montant',
  'Status': 'Statut',
  'Actions': 'Actions',
  'Not specified': 'Non précisé',
  'Missing receipt': 'Reçu manquant',
  'Owed to you': 'Montant qui vous est dû',
  'Receipt attached': 'Reçu joint',
  'Edit': 'Modifier',
  'Edit {{merchant}}': 'Modifier {{merchant}}',
  'Receivables · {{business}}': 'Comptes clients · {{business}}',
  'Create, send, and track professional client invoices.': 'Créez, envoyez et suivez des factures professionnelles pour vos clients.',
  'Recurring': 'Récurrentes',
  'New invoice': 'Nouvelle facture',
  'Invoice updated successfully.': 'La facture a été mise à jour.',
  'Unable to update invoice.': 'Impossible de mettre à jour la facture.',
  'Choose a Business before viewing invoices.': 'Choisissez une entreprise avant de consulter les factures.',
  'Loading invoices…': 'Chargement des factures…',
  'Unable to load invoices.': 'Impossible de charger les factures.',
  'No invoices yet': 'Aucune facture',
  'Create your first client invoice to get started.': 'Créez votre première facture client pour commencer.',
  'Create invoice': 'Créer une facture',
  'Invoice #': 'Facture no',
  'Client': 'Client',
  'Issue date': 'Date d’émission',
  'Due date': 'Date d’échéance',
  'No email': 'Aucun courriel',
  'Open actions for {{invoice}}': 'Ouvrir les actions pour {{invoice}}',
  'Duplicate': 'Dupliquer',
  'Mark as Paid': 'Marquer comme payée',
  'Void': 'Annuler',
  'Delete': 'Supprimer',
  'draft': 'brouillon',
  'scheduled': 'planifiée',
  'sent': 'envoyée',
  'paid': 'payée',
  'voided': 'annulée',
  'weekly': 'hebdomadaire',
  'monthly': 'mensuelle',
  'quarterly': 'trimestrielle',
  'yearly': 'annuelle',
  'Contractor workspace': 'Espace des travailleurs autonomes',
  'Bills': 'Factures fournisseurs',
  'Payments': 'Paiements',
  'Open bills': 'Factures ouvertes',
  'Overdue': 'En retard',
  'Unpaid': 'Impayée',
  'Partially paid': 'Partiellement payée',
  'Paid': 'Payée',
  'All bills': 'Toutes les factures',
  'Tax preparation': 'Préparation fiscale',
  'Tax Preparation Centre': 'Centre de préparation fiscale',
  'Choose a Business before opening the Tax Preparation Centre.': 'Choisissez une entreprise avant d’ouvrir le Centre de préparation fiscale.',
  'Organize filing records by their exact reporting period. SilaTrail does not calculate tax payable, determine deadlines, or submit returns.': 'Organisez les dossiers fiscaux selon leur période de déclaration exacte. SilaTrail ne calcule pas l’impôt à payer, ne détermine pas les échéances et ne produit pas les déclarations.',
  'Records package start': 'Début de la trousse de dossiers',
  'Records package end': 'Fin de la trousse de dossiers',
  'Building package…': 'Création de la trousse…',
  'Download records package': 'Télécharger la trousse de dossiers',
  'Neutral export format': 'Format d’exportation neutre',
  'Accounting-neutral v{{version}}': 'Format comptable neutre v{{version}}',
  'The ZIP also includes a versioned, vendor-neutral data folder for controlled accountant or integration workflows. It does not create journal entries or map records to QuickBooks, Xero or another accounting system.': 'Le fichier ZIP comprend aussi un dossier de données versionné et indépendant des fournisseurs pour les flux de travail contrôlés avec un comptable ou une intégration. Il ne crée pas d’écritures de journal et n’associe pas les dossiers à QuickBooks, Xero ou à un autre système comptable.',
  'Income tax': 'Impôt sur le revenu',
  'Federal tax records': 'Dossiers fiscaux fédéraux',
  'Selected reporting period': 'Période de déclaration sélectionnée',
  'Sales tax': 'Taxe de vente',
  'GST/HST reporting periods': 'Périodes de déclaration de TPS/TVH',
  'Keep a separate record for every monthly, quarterly, annual, or custom return.': 'Conservez un dossier distinct pour chaque déclaration mensuelle, trimestrielle, annuelle ou personnalisée.',
  'Add GST/HST period': 'Ajouter une période de TPS/TVH',
  'State and local obligations': 'Obligations étatiques et locales',
  'Filing route guidance': 'Conseils sur la méthode de déclaration',
  'GST/HST filing': 'Déclaration de TPS/TVH',
  'Checking records readiness…': 'Vérification de l’état des dossiers…',
  'The records readiness check is unavailable.': 'La vérification de l’état des dossiers n’est pas disponible.',
  'Update reporting period': 'Mettre à jour la période de déclaration',
  'New reporting period': 'Nouvelle période de déclaration',
  'Record filing': 'Enregistrer la déclaration',
  'Close filing form': 'Fermer le formulaire de déclaration',
  'SilaTrail organizes records. It does not provide tax advice, determine filing obligations or submit returns to tax authorities.': 'SilaTrail organise les dossiers. Il ne fournit pas de conseils fiscaux, ne détermine pas les obligations de déclaration et ne transmet pas de déclarations aux autorités fiscales.',
  'Review and export': 'Réviser et exporter',
  'Check income, expenses, and trips before exporting them for your accountant.': 'Vérifiez les revenus, les dépenses et les déplacements avant de les exporter pour votre comptable.',
  'Banking': 'Opérations bancaires',
  'Reconciliation workspace': 'Espace de rapprochement',
  'Search transactions': 'Rechercher des transactions',
  'Review status': 'Statut de révision',
  'Choose review category': 'Choisir une catégorie de révision',
  'Previous': 'Précédent',
  'Next': 'Suivant',
  'Search': 'Rechercher',
  'Clear': 'Effacer',
  'Apply': 'Appliquer',
  'Cancel': 'Annuler',
  'Save': 'Enregistrer',
  'Loading…': 'Chargement…',
  'Amount ({{currency}})': 'Montant ({{currency}})',
  'Remaining balance: {{amount}}': 'Solde restant : {{amount}}',
  'Payment date': 'Date du paiement',
  'Payment method': 'Mode de paiement',
  'Bank transfer': 'Virement bancaire',
  'Cheque': 'Chèque',
  'Cash': 'Comptant',
  'Other': 'Autre',
  'Reference number': 'Numéro de référence',
  'optional': 'facultatif',
  'Notes': 'Notes',
  'Proof of payment': 'Preuve de paiement',
  'PDF or image. Stored privately for this Business.': 'PDF ou image. Conservé de façon privée pour cette entreprise.',
  'Recording…': 'Enregistrement…',
  'Record payment': 'Enregistrer le paiement',
  'Bill details': 'Détails de la facture',
  'Identify the contractor and the period this bill belongs to.': 'Indiquez le travailleur autonome et la période visée par cette facture.',
  'Contractor': 'Travailleur autonome',
  'Choose a contractor': 'Choisir un travailleur autonome',
  'Bill or invoice number': 'Numéro de facture',
  'Bill date': 'Date de la facture',
  'The total is calculated automatically to keep the bill consistent.': 'Le total est calculé automatiquement afin de préserver la cohérence de la facture.',
  'Subtotal ({{currency}})': 'Sous-total ({{currency}})',
  'Tax ({{currency}})': 'Taxe ({{currency}})',
  'Calculated total': 'Total calculé',
  'Supporting information': 'Renseignements justificatifs',
  'Original bill': 'Facture originale',
  'Save bill': 'Enregistrer la facture',
  'Filing type': 'Type de déclaration',
  'GST/HST return': 'Déclaration de TPS/TVH',
  'Income tax return': 'Déclaration de revenus',
  'Reporting frequency': 'Fréquence de déclaration',
  'Monthly': 'Mensuelle',
  'Quarterly': 'Trimestrielle',
  'Annual': 'Annuelle',
  'Custom period': 'Période personnalisée',
  'Reporting period': 'Période de déclaration',
  'Start date': 'Date de début',
  'End date': 'Date de fin',
  'Filing due date': 'Date limite de production',
  'Preparing records': 'Préparation des dossiers',
  'Ready to file': 'Prête à produire',
  'Filed': 'Produite',
  'Assessed by CRA': 'Cotisée par l’ARC',
  'Filed date': 'Date de production',
  'CRA confirmation number': 'Numéro de confirmation de l’ARC',
  'Filed return or confirmation': 'Déclaration produite ou confirmation',
  'Notice of assessment': 'Avis de cotisation',
  'Save filing record': 'Enregistrer le dossier fiscal',
  'Records readiness': 'État de préparation des dossiers',
  'Ready': 'Prêt',
  'Monthly reviews': 'Révisions mensuelles',
  'Trip logs': 'Registres de déplacements',
  'Owner reimbursements': 'Remboursements au propriétaire',
  'Vehicle logbook': 'Registre des véhicules',
  'Expense categories': 'Catégories de dépenses',
  'New recurring invoice': 'Nouvelle facture récurrente',
  'Use a saved contractor': 'Utiliser un travailleur autonome enregistré',
  'Enter client manually': 'Saisir le client manuellement',
  'No saved contractors available': 'Aucun travailleur autonome enregistré',
  'Selecting a contractor copies their saved name and email. You can edit both fields for this schedule.': 'La sélection d’un travailleur autonome copie son nom et son courriel enregistrés. Vous pouvez modifier les deux champs pour cette planification.',
  'Client name': 'Nom du client',
  'Client email': 'Courriel du client',
  'Client address': 'Adresse du client',
  'Schedule': 'Calendrier',
  'Repeat': 'Répéter',
  'Weekly': 'Hebdomadaire',
  'Yearly': 'Annuelle',
  'First invoice': 'Première facture',
  'Payment terms (days)': 'Délai de paiement (jours)',
  'Line items': 'Articles',
  'Add item': 'Ajouter un article',
  'Qty': 'Qté',
  'Unit price': 'Prix unitaire',
  'Tax rate (%)': 'Taux de taxe (%)',
  'Invoice total': 'Total de la facture',
  'Automatically remind this client': 'Rappeler automatiquement ce client',
  'Days before due': 'Jours avant l’échéance',
  'Maximum overdue reminders': 'Nombre maximal de rappels de retard',
  'Creating…': 'Création…',
  'Start schedule': 'Démarrer le calendrier',
  'Recurring invoices': 'Factures récurrentes',
  'New schedule': 'Nouveau calendrier',
  'Back to invoices': 'Retour aux factures',
  'Active schedules': 'Calendriers actifs',
  'Next run': 'Prochaine exécution',
  'Reminder safety': 'Sécurité des rappels',
  'Loading schedules…': 'Chargement des calendriers…',
  'Unable to load recurring invoice schedules.': 'Impossible de charger les calendriers de factures récurrentes.',
  'No recurring invoices yet': 'Aucune facture récurrente',
  'active': 'actif',
  'paused': 'en pause',
  'Recurring expenses': 'Dépenses récurrentes',
  'Missing-receipt reminders': 'Rappels de reçus manquants',
  'Unable to load recurring expenses.': 'Impossible de charger les dépenses récurrentes.',
  'Add recurring expense': 'Ajouter une dépense récurrente',
  'Edit recurring expense': 'Modifier la dépense récurrente',
  'Schedule updated.': 'Le calendrier a été mis à jour.',
  'Recurring expense scheduled.': 'La dépense récurrente a été planifiée.',
  'No pending device records': 'Aucun dossier d’appareil en attente',
  'Check device records': 'Vérifier les dossiers de l’appareil',
  'Offline · saved on device': 'Hors ligne · enregistré sur l’appareil',
  'Syncing…': 'Synchronisation…',
  '{{count}} awaiting sync': '{{count}} en attente de synchronisation',
  'Office Supplies': 'Fournitures de bureau',
  'Software': 'Logiciels',
  'Travel': 'Déplacements',
  'Meals': 'Repas',
  'Tax filing': 'Déclaration fiscale',
  'Income Tax Return': 'Déclaration de revenus',
  'Federal tax return record': 'Dossier de déclaration fiscale fédérale',
  'GST/HST Return': 'Déclaration de TPS/TVH',
  'No reporting period recorded': 'Aucune période de déclaration enregistrée',
  'Recorded due date': 'Date d’échéance enregistrée',
  'Frequency': 'Fréquence',
  'Confirmation': 'Confirmation',
  'Update filing record': 'Mettre à jour le dossier de déclaration',
  'Record filing status': 'Enregistrer l’état de production',
  'You are not registered for GST/HST. Update Business settings if this changes.': 'Vous n’êtes pas inscrit à la TPS/TVH. Mettez à jour les paramètres de l’entreprise si la situation change.',
  'Not started': 'Non commencé',
  'Preparing': 'En préparation',
  'Assessed': 'Cotisée',
  'annual': 'annuelle',
  'custom': 'personnalisée',
  '{{start}} to {{end}}. Resolve open records before sharing the package.': 'Du {{start}} au {{end}}. Réglez les dossiers en suspens avant de partager la trousse.',
  '{{areas}} areas · {{items}} items': '{{areas}} sections · {{items}} éléments',
  'All {{count}} included months are marked Reviewed.': 'Les {{count}} mois inclus sont tous marqués comme révisés.',
  '{{reviewed}} of {{total}} included months reviewed; {{remaining}} remaining.': '{{reviewed}} mois révisés sur {{total}}; {{remaining}} restants.',
  'Every expense in this reporting period has a receipt.': 'Chaque dépense de cette période de déclaration possède un reçu.',
  '{{count}} expenses need a receipt or documented follow-up.': '{{count}} dépenses nécessitent un reçu ou un suivi documenté.',
  'Trip purpose, vehicle and distance fields are complete.': 'Les champs du motif, du véhicule et de la distance sont complets.',
  '{{count}} trips need more information.': '{{count}} déplacements nécessitent plus de renseignements.',
  'No unresolved owner repayment balances.': 'Aucun solde de remboursement au propriétaire non résolu.',
  '{{count}} owner balances remain unresolved.': '{{count}} soldes du propriétaire demeurent non résolus.',
  'Vehicles used by this period’s trips have valid odometer ranges.': 'Les véhicules utilisés pendant cette période ont des plages d’odomètre valides.',
  '{{count}} vehicle logs need odometer information.': '{{count}} registres de véhicule nécessitent des renseignements d’odomètre.',
  'Every expense has a category.': 'Chaque dépense possède une catégorie.',
  '{{count}} expenses need a category.': '{{count}} dépenses nécessitent une catégorie.',
  'The package includes transactions dated inside this inclusive period. For corporations, the suggested dates use the Business fiscal year end; verify first-year or shortened periods.': 'La trousse comprend les transactions datées dans cette période inclusive. Pour les sociétés, les dates suggérées utilisent la fin d’exercice de l’entreprise; vérifiez les premiers exercices ou les exercices abrégés.',
  'Add income tax record': 'Ajouter un dossier d’impôt sur le revenu',
  'Update income tax record': 'Mettre à jour le dossier d’impôt sur le revenu',
  'Add federal tax record': 'Ajouter un dossier fiscal fédéral',
  'Update federal tax record': 'Mettre à jour le dossier fiscal fédéral',
  'No GST/HST number is saved for this Business. Add it in Business settings before recording returns.': 'Aucun numéro de TPS/TVH n’est enregistré pour cette entreprise. Ajoutez-le dans les paramètres de l’entreprise avant d’enregistrer les déclarations.',
  'Corporate income tax (T2)': 'Impôt des sociétés (T2)',
  'A corporation generally files for its own fiscal period. Confirm the exact period and deadline with CRA or your tax professional before filing.': 'Une société produit généralement une déclaration pour son propre exercice. Confirmez la période exacte et l’échéance auprès de l’ARC ou de votre fiscaliste avant de produire.',
  'View CRA corporate filing guidance': 'Consulter les directives de l’ARC pour les sociétés',
  'Business income on a personal return': 'Revenu d’entreprise dans une déclaration personnelle',
  'Sole proprietors generally report business income and expenses on Form T2125 with their T1 return. Confirm what applies to your situation.': 'Les propriétaires uniques déclarent généralement leurs revenus et dépenses d’entreprise au moyen du formulaire T2125 avec leur déclaration T1. Confirmez ce qui s’applique à votre situation.',
  'View CRA Form T2125': 'Consulter le formulaire T2125 de l’ARC',
  'United States federal business taxes': 'Impôts fédéraux des entreprises aux États-Unis',
  'Federal obligations depend on the Business structure. State and local income, franchise, payroll and sales-tax requirements can also apply. Confirm the exact forms, periods and deadlines with the IRS, the relevant state agencies or a qualified tax professional.': 'Les obligations fédérales dépendent de la structure de l’entreprise. Des exigences étatiques et locales relatives au revenu, aux franchises, à la paie et à la taxe de vente peuvent aussi s’appliquer. Confirmez les formulaires, périodes et échéances auprès de l’IRS, des organismes d’État concernés ou d’un fiscaliste qualifié.',
  'View IRS business tax guidance': 'Consulter les directives fiscales de l’IRS',
  'Use the reporting period and due date shown in CRA My Business Account or confirmed by your tax professional.': 'Utilisez la période de déclaration et la date d’échéance indiquées dans Mon dossier d’entreprise de l’ARC ou confirmées par votre fiscaliste.',
  'View CRA GST/HST filing options': 'Consulter les options de production de la TPS/TVH de l’ARC',
  'Tax workflow not available for {{country}}': 'Processus fiscal non disponible pour {{country}}',
  'this country': 'ce pays',
  'Your financial records remain available. Canadian GST/HST and CRA guidance is hidden because it may not apply to this Business.': 'Vos dossiers financiers demeurent accessibles. Les directives canadiennes sur la TPS/TVH et l’ARC sont masquées puisqu’elles peuvent ne pas s’appliquer à cette entreprise.',
  'Country support will be enabled only after its tax terminology, currency rules and filing guidance are implemented and reviewed.': 'La prise en charge de ce pays sera activée seulement lorsque sa terminologie fiscale, ses règles monétaires et ses directives de déclaration auront été mises en œuvre et révisées.',
  'Match the frequency and dates shown in your CRA account.': 'Utilisez la fréquence et les dates indiquées dans votre compte de l’ARC.',
  'Use the exact first and last dates covered by this return.': 'Utilisez les dates exactes de début et de fin couvertes par cette déclaration.',
  'Enter the deadline from CRA or your tax professional; SilaTrail does not calculate it.': 'Saisissez l’échéance fournie par l’ARC ou votre fiscaliste; SilaTrail ne la calcule pas.',
  'Stored privately and visible only to authorized Business members.': 'Stocké de façon privée et visible uniquement par les membres autorisés de l’entreprise.',
  'Choose a date.': 'Choisissez une date.',
  'Use 120 characters or fewer.': 'Utilisez au plus 120 caractères.',
  'Use 2,000 characters or fewer.': 'Utilisez au plus 2 000 caractères.',
  'End date must be on or after the start date.': 'La date de fin doit être égale ou postérieure à la date de début.',
  'Due date cannot be before the reporting period ends.': 'La date d’échéance ne peut pas précéder la fin de la période de déclaration.',
  'Add the date the return was filed.': 'Ajoutez la date de production de la déclaration.',
  'Unable to create the records package.': 'Impossible de créer la trousse de dossiers.',
  '{{areas}} {{areaLabel}} · {{items}} {{itemLabel}}': '{{areas}} {{areaLabel}} · {{items}} {{itemLabel}}',
  'area': 'section',
  'areas': 'sections',
  'item': 'élément',
  'items': 'éléments',
  '1 expense needs a receipt or documented follow-up.': '1 dépense nécessite un reçu ou un suivi documenté.',
  '1 trip needs more information.': '1 déplacement nécessite plus de renseignements.',
  '1 owner balance remains unresolved.': '1 solde du propriétaire demeure non résolu.',
  '1 vehicle log needs odometer information.': '1 registre de véhicule nécessite des renseignements d’odomètre.',
  '1 expense needs a category.': '1 dépense nécessite une catégorie.',
  'Sales tax, income tax, franchise tax, payroll tax and registration requirements vary by state and locality. SilaTrail does not assume that a sales-tax return applies.': 'Les exigences relatives à la taxe de vente, à l’impôt sur le revenu, aux franchises, à la paie et à l’immatriculation varient selon l’État et la localité. SilaTrail ne présume pas qu’une déclaration de taxe de vente s’applique.',
  'Find official state tax websites': 'Trouver les sites fiscaux officiels des États',
  '{{start}} to {{end}}': 'Du {{start}} au {{end}}',
  'Private to your account · {{business}}': 'Privé dans votre compte · {{business}}',
  'Import statements and clear your review queue quickly. Matching never creates another expense, and every account stays with the selected Business.': 'Importez des relevés et traitez rapidement votre file de révision. Le rapprochement ne crée jamais une autre dépense et chaque compte demeure associé à l’entreprise sélectionnée.',
  'Close account form': 'Fermer le formulaire de compte',
  'Add account': 'Ajouter un compte',
  'Bank statement import is not available yet. Please try again after the next update.': 'L’importation de relevés bancaires n’est pas encore disponible. Réessayez après la prochaine mise à jour.',
  'Assign your existing accounts': 'Attribuer vos comptes existants',
  'These accounts were added before Business scoping. Assign only the accounts that belong to {{business}}.': 'Ces comptes ont été ajoutés avant la séparation par entreprise. Attribuez uniquement les comptes qui appartiennent à {{business}}.',
  'Personal / mixed use': 'Usage personnel ou mixte',
  'Assign to {{business}}': 'Attribuer à {{business}}',
  'Account nickname': 'Nom abrégé du compte',
  'Personal Visa • 1234': 'Visa personnelle • 1234',
  'Account currency': 'Devise du compte',
  'Account use': 'Utilisation du compte',
  'Use a nickname, not a full account number. Currency cannot be changed after creation.': 'Utilisez un nom abrégé, et non un numéro de compte complet. La devise ne peut plus être modifiée après la création.',
  'Adding…': 'Ajout…',
  'Save account': 'Enregistrer le compte',
  'Statement account': 'Compte du relevé',
  'Choose an account': 'Choisir un compte',
  'Close importer': 'Fermer l’importateur',
  'Import statement': 'Importer un relevé',
  'Loading your accounts…': 'Chargement de vos comptes…',
  'Add an account to import your first statement.': 'Ajoutez un compte pour importer votre premier relevé.',
  'Statement ready for review. No expenses were created.': 'Le relevé est prêt à être révisé. Aucune dépense n’a été créée.',
  'Transaction reconciliation': 'Rapprochement des transactions',
  '{{reviewed}} of {{total}} transactions reviewed': '{{reviewed}} transactions révisées sur {{total}}',
  '{{progress}}% complete': '{{progress}} % terminé',
  '{{progress}}% reviewed': '{{progress}} % révisé',
  'View statement': 'Afficher le relevé',
  'All statements': 'Tous les relevés',
  'Remove this imported statement and its transaction matches? Existing expenses will remain.': 'Supprimer ce relevé importé et ses rapprochements? Les dépenses existantes seront conservées.',
  'Remove': 'Supprimer',
  'Merchant, date, or exact amount': 'Commerçant, date ou montant exact',
  'All statuses': 'Tous les statuts',
  'All transactions': 'Toutes les transactions',
  'Review labels organize this statement; they do not post accounting entries.': 'Les étiquettes de révision organisent ce relevé; elles ne comptabilisent aucune écriture.',
  'Match outgoing money to an existing expense or add the missing expense. “Income to record” and “Refund or credit” remain follow-up labels until you create the appropriate record.': 'Rapprochez les sommes sortantes d’une dépense existante ou ajoutez la dépense manquante. « Revenu à enregistrer » et « Remboursement ou crédit » demeurent des étiquettes de suivi jusqu’à la création du dossier approprié.',
  'Checking receipts for strong matches…': 'Recherche de rapprochements fiables parmi les reçus…',
  'Review before saving': 'Vérifier avant d’enregistrer',
  'Suggested receipt matches': 'Rapprochements de reçus suggérés',
  'These suggestions have the same amount and currency, a nearby date, a strong merchant match, and an attached receipt. Nothing is matched until you confirm it.': 'Ces suggestions présentent le même montant et la même devise, une date rapprochée, une forte correspondance du commerçant et un reçu joint. Aucun rapprochement n’est enregistré avant votre confirmation.',
  'Confirming…': 'Confirmation…',
  'Confirm {{count}} selected': 'Confirmer les {{count}} sélections',
  'Select all strong suggestions': 'Sélectionner toutes les suggestions fiables',
  '1 suggested receipt match confirmed.': '1 rapprochement de reçu suggéré confirmé.',
  '{{count}} suggested receipt matches confirmed.': '{{count}} rapprochements de reçus suggérés confirmés.',
  'Select all reviewable transactions on this page': 'Sélectionner toutes les transactions révisables de cette page',
  '{{count}} selected': '{{count}} sélectionnées',
  'Only choices valid for every selected transaction are available.': 'Seuls les choix valides pour toutes les transactions sélectionnées sont disponibles.',
  'Bulk review category': 'Catégorie de révision groupée',
  'Choose a review category': 'Choisir une catégorie de révision',
  'Return to review': 'Renvoyer à la révision',
  'Loading transactions…': 'Chargement des transactions…',
  'Select {{description}}': 'Sélectionner {{description}}',
  'Matched to {{expense}}': 'Rapproché de {{expense}}',
  'linked expense': 'la dépense liée',
  'Receipt missing': 'Reçu manquant',
  'Match existing expense': 'Rapprocher une dépense existante',
  'Add missing expense': 'Ajouter la dépense manquante',
  'Undo match': 'Annuler le rapprochement',
  'Undo review': 'Annuler la révision',
  'Review category': 'Catégorie de révision',
  'You can undo this choice later. No expense or income record is created.': 'Vous pourrez annuler ce choix plus tard. Aucun dossier de dépense ou de revenu n’est créé.',
  'No transactions match this search. Try a merchant, YYYY-MM-DD date, or exact amount.': 'Aucune transaction ne correspond à cette recherche. Essayez un commerçant, une date AAAA-MM-JJ ou un montant exact.',
  'No {{status}} transactions in this view.': 'Aucune transaction « {{status}} » dans cette vue.',
  'No transactions here yet. Import a CSV or PDF to get started.': 'Aucune transaction ici pour le moment. Importez un fichier CSV ou PDF pour commencer.',
  'Account and Business currencies differ. Currency conversion and split matches are not supported in this version.': 'Les devises du compte et de l’entreprise diffèrent. La conversion de devises et les rapprochements fractionnés ne sont pas pris en charge dans cette version.',
  '{{count}} transactions · page {{page}}': '{{count}} transactions · page {{page}}',
  'Choose an expense match': 'Choisir une dépense à rapprocher',
  'Manual confirmation required': 'Confirmation manuelle requise',
  'Match {{description}}': 'Rapprocher {{description}}',
  'Candidates have the same amount and currency, with dates within seven days. SilaTrail ranks likely matches, but only you can confirm one.': 'Les choix proposés ont le même montant et la même devise, avec des dates à moins de sept jours. SilaTrail classe les rapprochements probables, mais vous seul pouvez en confirmer un.',
  'Finding candidates…': 'Recherche de choix possibles…',
  '{{confidence}} match': 'Rapprochement {{confidence}}',
  'Strong': 'fort',
  'Possible': 'possible',
  'Confirm match': 'Confirmer le rapprochement',
  'No eligible expenses found.': 'Aucune dépense admissible trouvée.',
  ', then return to match it. Check currency and payment method when recording a personal-card purchase.': ', puis revenez pour la rapprocher. Vérifiez la devise et le mode de paiement lorsque vous enregistrez un achat effectué avec une carte personnelle.',
  'Close matching': 'Fermer le rapprochement',
  'Account assigned to {{business}}': 'Compte attribué à {{business}}',
  'Transaction review saved. Accounting totals remain unchanged unless an expense is explicitly matched.': 'Révision de la transaction enregistrée. Les totaux comptables demeurent inchangés, sauf si une dépense est explicitement rapprochée.',
  '1 transaction marked {{status}}. Accounting totals are unchanged.': '1 transaction marquée « {{status}} ». Les totaux comptables sont inchangés.',
  '{{count}} transactions marked {{status}}. Accounting totals are unchanged.': '{{count}} transactions marquées « {{status}} ». Les totaux comptables sont inchangés.',
  'Statement and its matches removed. Expenses remain unchanged.': 'Le relevé et ses rapprochements ont été supprimés. Les dépenses demeurent inchangées.',
  'Needs review': 'À vérifier',
  'Matched expense': 'Dépense rapprochée',
  'Matched': 'Rapprochée',
  'Personal': 'Personnel',
  'Account transfer': 'Virement entre comptes',
  'Transfer': 'Virement',
  'Income to record': 'Revenu à enregistrer',
  'Refund or credit': 'Remboursement ou crédit',
  'Refund': 'Remboursement',
  'Exclude from bookkeeping': 'Exclure de la tenue de livres',
  'Excluded': 'Exclue',
  'No reconciliation decision has been saved yet.': 'Aucune décision de rapprochement n’a encore été enregistrée.',
  'Linked to an existing SilaTrail expense.': 'Liée à une dépense SilaTrail existante.',
  'Not a transaction for this Business.': 'Il ne s’agit pas d’une transaction de cette entreprise.',
  'Money moved between accounts; no new income or expense is created.': 'Sommes déplacées entre des comptes; aucun nouveau revenu ni aucune nouvelle dépense n’est créé.',
  'Incoming business money that still needs an income record.': 'Sommes reçues par l’entreprise qui nécessitent encore un dossier de revenu.',
  'Incoming money that may relate to an earlier purchase or adjustment.': 'Sommes reçues pouvant être liées à un achat antérieur ou à un rajustement.',
  'Intentionally ignored in this reconciliation workspace.': 'Intentionnellement ignorée dans cet espace de rapprochement.',
  'Bank CSV': 'CSV bancaire',
  'Automatic CSV presets: RBC, TD Canada Trust, Scotiabank, BMO, CIBC, ATB Financial and Tangerine. You will still review every mapping before import.': 'Préréglages CSV automatiques : RBC, TD Canada Trust, Banque Scotia, BMO, CIBC, ATB Financial et Tangerine. Vous vérifierez tout de même chaque association avant l’importation.',
  'We mapped the columns, date format and money direction for you.': 'Nous avons associé les colonnes, le format de date et le sens des fonds pour vous.',
  'We filled the fields we recognized. Check the remaining mapping before continuing.': 'Nous avons rempli les champs reconnus. Vérifiez les associations restantes avant de continuer.',
  '{{subject}} detected. {{setup}}': '{{subject}} détecté. {{setup}}',
  '{{format}} detected. We verified {{count}} transactions against the statement totals.': '{{format}} détecté. Nous avons vérifié {{count}} transactions par rapport aux totaux du relevé.',
  'Choose a CSV or PDF statement to continue.': 'Choisissez un relevé CSV ou PDF pour continuer.',
  'Choose a CSV or PDF bank statement.': 'Choisissez un relevé bancaire CSV ou PDF.',
  'RBC personal banking PDFs are currently supported only for CAD statement accounts. Choose a CAD account or use CSV.': 'Les PDF de services bancaires personnels RBC sont actuellement pris en charge uniquement pour les comptes de relevé en CAD. Choisissez un compte en CAD ou utilisez un fichier CSV.',
  'Map the required columns and choose the correct date format before previewing.': 'Associez les colonnes requises et choisissez le bon format de date avant l’aperçu.',
  'Include at least one transaction.': 'Incluez au moins une transaction.',
  'Choose column': 'Choisir une colonne',
  'Import bank statement': 'Importer un relevé bancaire',
  'Import a statement': 'Importer un relevé',
  'For {{account}} · {{currency}}. Files stay on your device; only the reviewed transaction rows are saved. Use one currency per account.': 'Pour {{account}} · {{currency}}. Les fichiers demeurent sur votre appareil; seules les lignes de transactions révisées sont enregistrées. Utilisez une seule devise par compte.',
  'Import progress': 'Progression de l’importation',
  'Upload': 'Téléverser',
  'Confirm mapping': 'Confirmer les associations',
  'Review and import': 'Réviser et importer',
  'CSV or PDF statement': 'Relevé CSV ou PDF',
  'CSV: UTF-8 or UTF-16, up to 1,000 transactions and 2 MB. PDF: searchable RBC personal banking statement, up to 24 pages and 10 MB. Files are processed on this device.': 'CSV : UTF-8 ou UTF-16, jusqu’à 1 000 transactions et 2 Mo. PDF : relevé bancaire personnel RBC consultable, jusqu’à 24 pages et 10 Mo. Les fichiers sont traités sur cet appareil.',
  'Download an example CSV': 'Télécharger un exemple CSV',
  'CSV separator': 'Séparateur CSV',
  'Comma': 'Virgule',
  'Semicolon': 'Point-virgule',
  'Tab': 'Tabulation',
  'Detected automatically. Change this only if your bank exported a different separator.': 'Détecté automatiquement. Modifiez-le seulement si votre banque a exporté un séparateur différent.',
  'We could not prepare this statement.': 'Nous n’avons pas pu préparer ce relevé.',
  'Date format': 'Format de date',
  'Year / Month / Day': 'Année / Mois / Jour',
  'Day / Month / Year': 'Jour / Mois / Année',
  'Month / Day / Year': 'Mois / Jour / Année',
  'Amount columns': 'Colonnes de montant',
  'One signed amount column': 'Une colonne de montant signé',
  'Separate debit and credit columns': 'Colonnes de débit et de crédit distinctes',
  'Decimal separator': 'Séparateur décimal',
  'Point: 1,234.56': 'Point : 1 234,56',
  'Comma: 1.234,56': 'Virgule : 1 234,56',
  'Transaction date': 'Date de la transaction',
  'Additional description (optional)': 'Description supplémentaire (facultative)',
  'Money out / debit': 'Sommes sortantes / débit',
  'Money in / credit': 'Sommes entrantes / crédit',
  'In this file, positive amounts mean money spent (common for credit cards).': 'Dans ce fichier, les montants positifs représentent des sommes dépensées (courant pour les cartes de crédit).',
  'Review normalized bank descriptions': 'Réviser les descriptions bancaires normalisées',
  'Rows {{rows}}{{others}} contained unquoted commas. The complete description was preserved and the amount columns were not changed.': 'Les lignes {{rows}}{{others}} contenaient des virgules sans guillemets. La description complète a été conservée et les colonnes de montant n’ont pas été modifiées.',
  ' and others': ' et d’autres',
  '1 row will be excluded until corrected.': '1 ligne sera exclue jusqu’à sa correction.',
  '{{count}} rows will be excluded until corrected.': '{{count}} lignes seront exclues jusqu’à leur correction.',
  'Row {{row}}: {{error}}': 'Ligne {{row}} : {{error}}',
  'Review the mapping or continue with the {{count}} valid rows.': 'Révisez les associations ou poursuivez avec les {{count}} lignes valides.',
  'Ready to preview 1 valid transaction{{excluded}}.': 'Prêt à afficher l’aperçu de 1 transaction valide{{excluded}}.',
  'Ready to preview {{count}} valid transactions{{excluded}}.': 'Prêt à afficher l’aperçu de {{count}} transactions valides{{excluded}}.',
  ' · {{count}} excluded': ' · {{count}} exclues',
  'Preview and check overlaps': 'Afficher l’aperçu et vérifier les doublons',
  'Statement totals verified': 'Totaux du relevé vérifiés',
  '{{start}} to {{end}} · {{count}} transactions · {{withdrawals}} {{currency}} withdrawn · {{deposits}} {{currency}} deposited.': 'Du {{start}} au {{end}} · {{count}} transactions · {{withdrawals}} {{currency}} retirés · {{deposits}} {{currency}} déposés.',
  'Ready to preview 1 verified transaction. PDF columns and money direction are locked because they were checked against the statement totals.': 'Prêt à afficher l’aperçu de 1 transaction vérifiée. Les colonnes PDF et le sens des fonds sont verrouillés puisqu’ils ont été vérifiés par rapport aux totaux du relevé.',
  'Ready to preview {{count}} verified transactions. PDF columns and money direction are locked because they were checked against the statement totals.': 'Prêt à afficher l’aperçu de {{count}} transactions vérifiées. Les colonnes PDF et le sens des fonds sont verrouillés puisqu’ils ont été vérifiés par rapport aux totaux du relevé.',
  'Review {{count}} transactions': 'Réviser {{count}} transactions',
  'Negative amounts are money out; positive amounts are money in. Nothing becomes an expense automatically. Possible overlaps are flagged, never silently removed.': 'Les montants négatifs représentent des sorties de fonds; les montants positifs, des entrées. Rien ne devient automatiquement une dépense. Les doublons possibles sont signalés, jamais supprimés silencieusement.',
  'Include': 'Inclure',
  'Possible prior import': 'Importation antérieure possible',
  'Repeated in this file': 'Répétée dans ce fichier',
  'Include statement row {{row}}': 'Inclure la ligne {{row}} du relevé',
  'Page {{page}} of {{pages}}': 'Page {{page}} sur {{pages}}',
  'I checked the dates, money direction, {{currency}} currency and possible overlaps. Import the {{count}} included rows.': 'J’ai vérifié les dates, le sens des fonds, la devise {{currency}} et les doublons possibles. Importer les {{count}} lignes incluses.',
  'Import reviewed transactions': 'Importer les transactions révisées',
  'Processing your statement…': 'Traitement de votre relevé…',
  "Add a vehicle": "Ajouter un véhicule",
  "Add fuel consumption to the related vehicle. Electric vehicles remain excluded.": "Ajoutez la consommation de carburant au véhicule concerné. Les véhicules électriques demeurent exclus.",
  "Add the city and province or state for better matches.": "Ajoutez la ville et la province ou l’État pour obtenir de meilleurs résultats.",
  "Business use": "Utilisation professionnelle",
  "Business use %": "Pourcentage d’utilisation professionnelle",
  "Calculated distance": "Distance calculée",
  "Capture business travel without creating an expense.": "Consignez vos déplacements professionnels sans créer de dépense.",
  "Choose a Business before recording a trip.": "Choisissez une entreprise avant d’enregistrer un déplacement.",
  "Choose a saved trip…": "Choisir un déplacement enregistré…",
  "Choose a method, then enter the distance information below.": "Choisissez une méthode, puis saisissez les renseignements sur la distance ci-dessous.",
  "Choose both a start location and destination to calculate the route.": "Choisissez un lieu de départ et une destination pour calculer l’itinéraire.",
  "Client or project": "Client ou projet",
  "Destination": "Destination",
  "Describe the trip and choose the business-use classification that matches it.": "Décrivez le déplacement et choisissez la classification d’utilisation professionnelle qui lui correspond.",
  "Distance": "Distance",
  "Distance (km)": "Distance (km)",
  "Distance ({{unit}})": "Distance ({{unit}})",
  "Distance method": "Méthode de calcul de la distance",
  "End km": "Kilométrage de fin",
  "End {{unit}}": "Odomètre de fin ({{unit}})",
  "Enter an end odometer greater than the starting reading.": "Saisissez une lecture d’odomètre de fin supérieure à celle du départ.",
  "Estimated driving distance": "Distance de conduite estimée",
  "Estimated driving time": "Durée de conduite estimée",
  "From {{place}}": "Depuis {{place}}",
  "Fuel estimates use your entered price and recorded vehicle consumption. Trips without consumption and electric vehicles are excluded. Record actual fuel receipts in Expenses; this estimate is not a tax deduction or mileage reimbursement.": "Les estimations de carburant utilisent le prix saisi et la consommation enregistrée du véhicule. Les déplacements sans consommation et les véhicules électriques sont exclus. Enregistrez les reçus de carburant réels dans Dépenses; cette estimation ne constitue ni une déduction fiscale ni un remboursement kilométrique.",
  "How do you want to record distance?": "Comment souhaitez-vous enregistrer la distance?",
  "How was this trip used?": "Quelle était l’utilisation de ce déplacement?",
  "Location access is blocked": "L’accès à la localisation est bloqué",
  "Location is always opt-in": "La localisation est toujours facultative",
  "Locations": "Lieux",
  "Main office": "Bureau principal",
  "Manage shortcuts": "Gérer les raccourcis",
  "No vehicles yet": "Aucun véhicule pour le moment",
  "Office shortcuts": "Raccourcis du bureau",
  "Office to warehouse": "Du bureau à l’entrepôt",
  "On iPhone, open Settings, choose Safari (or SilaTrail), then Location and select While Using. In other browsers, open this site’s settings and allow Location.": "Sur iPhone, ouvrez Réglages, choisissez Safari (ou SilaTrail), puis Localisation et sélectionnez Lorsque l’app est active. Dans les autres navigateurs, ouvrez les paramètres de ce site et autorisez la localisation.",
  "Place type": "Type de lieu",
  "Private to your account in this Business.": "Privé dans votre compte pour cette entreprise.",
  "Quick trip": "Déplacement rapide",
  "Receipt attached: {{filename}}": "Reçu joint : {{filename}}",
  "Regular home-to-work travel is generally personal commuting. Choose the option that matches the actual trip.": "Les déplacements réguliers entre le domicile et le travail sont généralement personnels. Choisissez l’option qui correspond au déplacement réel.",
  "Remove saved place {{place}}": "Supprimer le lieu enregistré {{place}}",
  "Remove receipt": "Supprimer le reçu",
  "Remove trip template {{template}}": "Supprimer le modèle de déplacement {{template}}",
  "Remove {{place}}? Any templates using it will also be removed.": "Supprimer {{place}}? Tous les modèles qui l’utilisent seront également supprimés.",
  "Remove {{template}}?": "Supprimer {{template}}?",
  "Reuse a route or fill the office side first. Distance is always recalculated.": "Réutilisez un itinéraire ou remplissez d’abord le côté du bureau. La distance est toujours recalculée.",
  "Review vehicle details": "Vérifier les détails du véhicule",
  "Route": "Itinéraire",
  "Route supplied by": "Itinéraire fourni par",
  "Save an office or route below to create one-tap shortcuts.": "Enregistrez un bureau ou un itinéraire ci-dessous pour créer des raccourcis accessibles en un geste.",
  "Save this route as a template": "Enregistrer cet itinéraire comme modèle",
  "Save this {{label}}": "Enregistrer ce {{label}}",
  "Save {{label}} for later": "Enregistrer {{label}} pour plus tard",
  "Saved places": "Lieux enregistrés",
  "Saved trip template": "Modèle de déplacement enregistré",
  "Search address or place": "Rechercher une adresse ou un lieu",
  "Search instead": "Rechercher plutôt",
  "Search is prioritized within {{country}}. ": "La recherche est priorisée au {{country}}. ",
  "Search results": "Résultats de recherche",
  "Short name": "Nom abrégé",
  "SilaTrail asks for your device location only when you tap “Use current location.” It does not track trips in the background. Saved places are private to your account.": "SilaTrail demande la position de votre appareil uniquement lorsque vous touchez « Utiliser la position actuelle ». Aucun déplacement n’est suivi en arrière-plan. Les lieux enregistrés sont privés dans votre compte.",
  "Some trips cannot be included in the fuel estimate.": "Certains déplacements ne peuvent pas être inclus dans l’estimation du carburant.",
  "Start km": "Kilométrage de départ",
  "Start {{unit}}": "Odomètre de départ ({{unit}})",
  "Start": "Départ",
  "Start location": "Lieu de départ",
  "The route, vehicle, purpose and classification are reused; distance is recalculated each time.": "L’itinéraire, le véhicule, le motif et la classification sont réutilisés; la distance est recalculée chaque fois.",
  "To preserve mileage and odometer integrity, contact support if a saved trip needs correction.": "Pour préserver l’intégrité du kilométrage et de l’odomètre, communiquez avec le soutien si un déplacement enregistré doit être corrigé.",
  "To {{place}}": "Vers {{place}}",
  "Trip details": "Détails du déplacement",
  "Trip purpose": "Motif du déplacement",
  "Trip shortcuts are temporarily unavailable. You can still record the trip normally.": "Les raccourcis de déplacement sont temporairement indisponibles. Vous pouvez tout de même enregistrer le déplacement normalement.",
  "Trip template name": "Nom du modèle de déplacement",
  "Trip templates": "Modèles de déplacement",
  "Try location again": "Réessayer la localisation",
  "Use template": "Utiliser le modèle",
  "Use my location as start": "Utiliser ma position comme départ",
  "Use my location as destination": "Utiliser ma position comme destination",
  "View trip details": "Afficher les détails du déplacement",
  "Review and record": "Vérifier et enregistrer",
  "Add an optional note, check the business distance, then record the trip.": "Ajoutez une note facultative, vérifiez la distance professionnelle, puis enregistrez le déplacement.",
  "You can record a manual trip or add a vehicle for odometer tracking.": "Vous pouvez enregistrer un déplacement manuellement ou ajouter un véhicule pour le suivi par odomètre.",
  "You have read-only accountant access to this Business. Only an owner or administrator can record a trip.": "Votre accès de comptable à cette entreprise est en lecture seule. Seul un propriétaire ou un administrateur peut enregistrer un déplacement.",
  "Your session has expired.": "Votre session a expiré.",
  "using OpenStreetMap data. Confirm it matches the trip you actually made.": "à l’aide des données OpenStreetMap. Confirmez qu’il correspond au trajet réellement effectué.",
  "{{label}} saved places": "Lieux {{label}} enregistrés",
  "{{label}} search results": "Résultats de recherche pour {{label}}",
  "{{percentage}}% business use": "{{percentage}} % d’utilisation professionnelle",
  "Client visit": "Visite chez un client",
  "Business meeting": "Réunion d’affaires",
  "Site inspection": "Inspection de chantier",
  "Delivery": "Livraison",
  "Supplier or inventory pickup": "Ramassage chez un fournisseur ou de stocks",
  "Banking or business errand": "Course bancaire ou professionnelle",
  "Training or conference": "Formation ou conférence",
  "Travel between workplaces": "Déplacement entre lieux de travail",
  "Regular workplace commute": "Trajet régulier domicile-travail",
  "Best for vehicle logbooks": "Idéal pour les registres de véhicule",
  "Start and destination": "Départ et destination",
  "Manual entry": "Saisie manuelle",
  "Enter known distance": "Saisir une distance connue",
  "100% business use": "Utilisation professionnelle à 100 %",
  "Commute / personal": "Trajet domicile-travail / personnel",
  "0% business use": "Utilisation professionnelle à 0 %",
  "Mixed": "Mixte",
  "Choose a percentage": "Choisir un pourcentage",
  "Please select a date.": "Veuillez sélectionner une date.",
  "Please select a purpose.": "Veuillez sélectionner un motif.",
  "Choose a start and destination.": "Choisissez un point de départ et une destination.",
  "Calculate the route before saving.": "Calculez l’itinéraire avant d’enregistrer.",
  "Choose how this trip was used.": "Indiquez l’utilisation de ce déplacement.",
  "required": "obligatoire",
  "No vehicle": "Aucun véhicule",
  "Enter distance": "Saisir la distance",
  "Calculate route": "Calculer l’itinéraire",
  "Save template": "Enregistrer le modèle",
  "Use current location": "Utiliser la position actuelle",
  "Current location": "Position actuelle",
  "We found your location, but could not name the address. Current location was selected; search for an address if you need a more specific label.": "Votre position a été trouvée, mais l’adresse n’a pas pu être déterminée. La position actuelle a été sélectionnée; recherchez une adresse si vous avez besoin d’un libellé plus précis.",
  "Searching…": "Recherche…",
  "Save place": "Enregistrer le lieu",
  "Home office": "Bureau à domicile",
  "Client site": "Site du client",
  "Warehouse": "Entrepôt",
  "Supplier": "Fournisseur",
  "Trip saved.": "Déplacement enregistré.",
  "Unable to save trip. Please try again.": "Impossible d’enregistrer le déplacement. Veuillez réessayer.",
  "Record Trip": "Enregistrer un déplacement",
  "Set automatically from the trip classification.": "Défini automatiquement selon la classification du déplacement.",
  "Enter the business portion of this trip.": "Saisissez la part professionnelle de ce déplacement.",
  '{{country}} accountant pack': 'Trousse comptable — {{country}}',
  'Country-specific': 'Pays sélectionné',
  'Includes jurisdiction context, recorded expense tax labels and country-appropriate mileage columns. It organizes records but does not calculate tax or determine filing obligations.': 'Comprend le contexte de la juridiction, les libellés fiscaux enregistrés pour les dépenses et des colonnes de kilométrage adaptées au pays. La trousse organise les dossiers, mais ne calcule pas l’impôt et ne détermine pas les obligations de production.',
  'Needs attention': 'À vérifier',
  'One place for records that still need a decision, receipt, or response. Nothing is changed automatically.': 'Un seul endroit pour les dossiers qui nécessitent encore une décision, un reçu ou une réponse. Rien n’est modifié automatiquement.',
  '{{count}} items': '{{count}} éléments',
  'Loading items…': 'Chargement des éléments…',
  'Unable to load items that need attention. Please try again.': 'Impossible de charger les éléments à vérifier. Veuillez réessayer.',
  'Choose a Business before reviewing items that need attention.': 'Choisissez une entreprise avant de consulter les éléments à vérifier.',
  'You are all caught up': 'Tout est à jour',
  'Ready for month-end review': 'Prêt pour la révision de fin de mois',
  'Month-end review has open items': 'La révision de fin de mois comporte des éléments ouverts',
  'Review period': 'Période de révision',
  'All open': 'Tous les éléments ouverts',
  'This month': 'Ce mois-ci',
  'Today': 'Aujourd’hui',
  '{{count}} days open': 'Ouvert depuis {{count}} jours',
  'Age unavailable': 'Âge indisponible',
  'Review acknowledgements': 'Confirmations de révision',
  'I reviewed the income, expenses, trips, and supporting records for this month.': 'J’ai examiné les revenus, les dépenses, les déplacements et les pièces justificatives de ce mois.',
  'I resolved the open review items shown for this month.': 'J’ai réglé les éléments de révision ouverts affichés pour ce mois.',
  'I reviewed contractor bills, payments, and owner repayments.': 'J’ai examiné les factures et paiements des travailleurs autonomes ainsi que les remboursements au propriétaire.',
  'I understand this records my review only; it is not tax filing or accountant certification.': 'Je comprends que ceci consigne uniquement ma révision; il ne s’agit ni d’une déclaration fiscale ni d’une certification comptable.',
  '{{count}} review items remain open.': '{{count}} éléments de révision demeurent ouverts.',
  'Open Needs attention': 'Ouvrir À vérifier',
  'Only the Business owner can complete the month-end sign-off.': 'Seul le propriétaire de l’entreprise peut effectuer la confirmation de fin de mois.',
  'Needs re-review': 'Nouvelle révision requise',
  'This month changed after review': 'Ce mois a changé après la révision',
  'The saved review no longer matches the current records or exception state. Resolve any open items and complete the review again.': 'La révision enregistrée ne correspond plus aux dossiers ou aux exceptions actuels. Réglez les éléments ouverts et effectuez de nouveau la révision.',
  'Complete review again': 'Effectuer de nouveau la révision',
  'No current reconciliation exceptions are blocking review for this Business.': 'Aucune exception de rapprochement actuelle ne bloque la révision de cette entreprise.',
  '{{count}} items still need a decision, document, or response before review.': '{{count}} éléments nécessitent encore une décision, un document ou une réponse avant la révision.',
  'There are no missing receipts, unmatched bank transactions, review drafts, or open accountant requests for this Business.': 'Il n’y a aucun reçu manquant, aucune transaction bancaire non rapprochée, aucun brouillon à réviser ni aucune demande ouverte du comptable pour cette entreprise.',
  'There are no missing receipts, unmatched bank transactions, or open accountant requests for this Business.': 'Il n’y a aucun reçu manquant, aucune transaction bancaire non rapprochée ni aucune demande ouverte du comptable pour cette entreprise.',
  'Missing receipts': 'Reçus manquants',
  'Expenses recorded without a receipt that have not been acknowledged.': 'Dépenses enregistrées sans reçu qui n’ont pas été confirmées.',
  'Unmatched transactions': 'Transactions non rapprochées',
  'Imported bank transactions that still need a bookkeeping decision.': 'Transactions bancaires importées qui nécessitent encore une décision comptable.',
  'Expense drafts': 'Brouillons de dépenses',
  'Expense draft': 'Brouillon de dépense',
  'Bank-derived expense drafts waiting for approval or correction.': 'Brouillons de dépenses provenant de la banque en attente d’approbation ou de correction.',
  'Income drafts': 'Brouillons de revenus',
  'Income draft': 'Brouillon de revenu',
  'Bank-derived income drafts waiting for approval or correction.': 'Brouillons de revenus provenant de la banque en attente d’approbation ou de correction.',
  'Questions from your accountant that are not resolved or cancelled.': 'Questions de votre comptable qui ne sont ni résolues ni annulées.',
  "Review emailed receipt": "Réviser le reçu envoyé par courriel",
  "Record recurring expense": "Enregistrer la dépense récurrente",
  "Capture one expense at a time. Review every suggestion before saving.": "Saisissez une dépense à la fois. Révisez chaque suggestion avant l’enregistrement.",
  "Expense capture steps": "Étapes de saisie de la dépense",
  "Add receipt": "Ajouter le reçu",
  "Review details": "Réviser les détails",
  "Confirm and save": "Confirmer et enregistrer",
  "Step 1": "Étape 1",
  "Step 2": "Étape 2",
  "Step 3": "Étape 3",
  "Step 4": "Étape 4",
  "Receipt or source": "Reçu ou source",
  "Attach a receipt for your records, or continue with manual entry.": "Joignez un reçu à vos dossiers ou poursuivez avec la saisie manuelle.",
  "Expense details": "Détails de la dépense",
  "Confirm the merchant, total, date and category.": "Confirmez le commerçant, le total, la date et la catégorie.",
  "Merchant name": "Nom du commerçant",
  "e.g. Air Canada": "p. ex. Air Canada",
  "Merchant name is required.": "Le nom du commerçant est obligatoire.",
  "Amount must be greater than $0.00.": "Le montant doit être supérieur à 0,00 $.",
  "Payment and context": "Paiement et contexte",
  "Record how it was paid and add anything your reviewer should know.": "Indiquez le mode de paiement et ajoutez tout renseignement utile à la personne qui révise.",
  "Add new card": "Ajouter une carte",
  "Choose a payment method to record whether personal or Business funds were used.": "Choisissez un mode de paiement pour indiquer si des fonds personnels ou ceux de l’entreprise ont été utilisés.",
  "(optional)": "(facultatif)",
  "Add context for this expense": "Ajoutez du contexte pour cette dépense",
  "We check for possible duplicates before anything is saved.": "Nous vérifions les doublons possibles avant tout enregistrement.",
  "Nothing is saved until you confirm.": "Rien n’est enregistré avant votre confirmation.",
  "Checking duplicates…": "Vérification des doublons…",
  "Save expense": "Enregistrer la dépense",
  "Receipt reading": "Lecture du reçu",
  "Read your receipt": "Lire votre reçu",
  "Suggestions only": "Suggestions seulement",
  "Suggestions come from the email’s plain text. Forwarded messages may contain several totals; check the original receipt.": "Les suggestions proviennent du texte brut du courriel. Les messages transférés peuvent contenir plusieurs totaux; vérifiez le reçu original.",
  "Use a clear photo with the full receipt visible. Reading stays on this device; connect to load the reader.": "Utilisez une photo nette montrant le reçu en entier. La lecture demeure sur cet appareil; connectez-vous pour charger le lecteur.",
  "Photo rotation": "Rotation de la photo",
  "Original orientation": "Orientation originale",
  "90° clockwise": "90° dans le sens horaire",
  "180° upside down": "180° à l’envers",
  "90° counterclockwise": "90° dans le sens antihoraire",
  "If the photo is sideways, rotate it until the text is upright, then choose Read receipt. The original stays unchanged.": "Si la photo est de côté, faites-la pivoter jusqu’à ce que le texte soit droit, puis choisissez Lire le reçu. L’original demeure inchangé.",
  "Reading… {{progress}}%": "Lecture… {{progress}} %",
  "Loading reader…": "Chargement du lecteur…",
  "Read receipt": "Lire le reçu",
  "Cancel reading": "Annuler la lecture",
  "You can still enter details and save the receipt offline.": "Vous pouvez toujours saisir les détails et enregistrer le reçu hors ligne.",
  "Connect to the internet to save this emailed receipt.": "Connectez-vous à Internet pour enregistrer ce reçu envoyé par courriel.",
  "Reading the image. Difficult photos may need a second pass. You can cancel at any time.": "Lecture de l’image. Les photos difficiles peuvent nécessiter un second passage. Vous pouvez annuler en tout temps.",
  "Review suggested details": "Réviser les détails suggérés",
  "Some text was difficult to read. ": "Une partie du texte était difficile à lire. ",
  "Check every field against the original; suggestions are not verified.": "Comparez chaque champ à l’original; les suggestions ne sont pas vérifiées.",
  "Merchant": "Commerçant",
  "Total paid": "Total payé",
  "Receipt date": "Date du reçu",
  "Receipt currency (e.g. CAD)": "Devise du reçu (p. ex. CAD)",
  "Tax shown (optional)": "Taxe indiquée (facultatif)",
  "This receipt says {{receiptCurrency}}; your Business uses {{businessCurrency}}. The total will not be copied. Enter the converted amount manually and keep the original currency in your notes.": "Ce reçu indique {{receiptCurrency}}, tandis que votre entreprise utilise {{businessCurrency}}. Le total ne sera pas copié. Saisissez manuellement le montant converti et conservez la devise originale dans vos notes.",
  "Tax and currency are saved as reviewed receipt notes. Tax is not automatically claimed or added to the total.": "La taxe et la devise sont enregistrées comme notes de reçu révisées. La taxe n’est ni réclamée automatiquement ni ajoutée au total.",
  "I checked these suggestions against the receipt.": "J’ai comparé ces suggestions au reçu.",
  "Use reviewed details": "Utiliser les détails révisés",
  "Show extracted text": "Afficher le texte extrait",
  "No text found. Try a clearer photo or enter details manually.": "Aucun texte trouvé. Essayez une photo plus nette ou saisissez les détails manuellement.",
  "Expense summary": "Résumé des dépenses",
  "Recorded total": "Total enregistré",
  "Latest {{count}} records": "{{count}} derniers dossiers",
  "Need follow-up": "Suivi nécessaire",
  "Personal payments": "Paiements personnels",
  "Included in Owed to you": "Inclus dans Sommes qui vous sont dues",
  "Search expenses": "Rechercher des dépenses",
  "Search merchant, category or notes": "Rechercher un commerçant, une catégorie ou des notes",
  "Filter expense records": "Filtrer les dossiers de dépenses",
  "All records": "Tous les dossiers",
  "No matching expenses": "Aucune dépense correspondante",
  "Try a different search or record filter.": "Essayez une autre recherche ou un autre filtre.",
  "Clear filters": "Effacer les filtres",
  "View record": "Voir le dossier",
  "View {{merchant}}": "Voir {{merchant}}",
  "View": "Voir",
  "Expense record": "Dossier de dépense",
  "Edit expense": "Modifier la dépense",
  "Close expense record": "Fermer le dossier de dépense",
  "Recorded amount": "Montant enregistré",
  "Record details": "Détails du dossier",
  "Payment source": "Source du paiement",
  "Personal funds": "Fonds personnels",
  "Business funds or not specified": "Fonds de l’entreprise ou non précisé",
  "Receipt": "Reçu",
  "Current expense receipt": "Reçu actuel de la dépense",
  "Open private receipt": "Ouvrir le reçu privé",
  "Preparing private receipt…": "Préparation du reçu privé…",
  "Receipt preview is unavailable.": "L’aperçu du reçu n’est pas disponible.",
  "No receipt attached": "Aucun reçu joint",
  "Edit this record to add the missing receipt.": "Modifiez ce dossier pour ajouter le reçu manquant.",
  "Edit record": "Modifier le dossier",
  "Cancel editing": "Annuler la modification",
  "Saving changes…": "Enregistrement des modifications…",
  "Date is required.": "La date est obligatoire.",
  "View current receipt": "Voir le reçu actuel",
  "New receipt selected: {{name}}": "Nouveau reçu sélectionné : {{name}}",
  "Choose a new file only if you want to replace the current receipt.": "Choisissez un nouveau fichier seulement pour remplacer le reçu actuel.",
  "Add a JPG, PNG, WebP or PDF receipt.": "Ajoutez un reçu JPG, PNG, WebP ou PDF.",
  "Payment classification": "Classification du paiement",
  "This expense is included in your Owed to you balance.": "Cette dépense est incluse dans votre solde Sommes qui vous sont dues.",
  "This expense is not marked as a personal payment.": "Cette dépense n’est pas marquée comme paiement personnel.",
  "Unable to update this expense.": "Impossible de modifier cette dépense.",
  "Monthly review steps": "Étapes de la révision mensuelle",
  "Choose period": "Choisir la période",
  "Resolve issues": "Résoudre les éléments",
  "Finish review": "Terminer la révision",
  "Export records": "Exporter les dossiers",
  "Step 1 · Period": "Étape 1 · Période",
  "Choose the month to review": "Choisir le mois à réviser",
  "Reviewed": "Révisé",
  "In progress": "En cours",
  "{{count}} areas need attention": "{{count}} éléments nécessitent votre attention",
  "Amounts use {{currency}}. Contractor bills and payments are exported separately from expense totals.": "Les montants sont en {{currency}}. Les factures et paiements des travailleurs autonomes sont exportés séparément des totaux de dépenses.",
  "Loading monthly records…": "Chargement des dossiers mensuels…",
  "Income total": "Total des revenus",
  "{{count}} income records": "{{count}} dossiers de revenus",
  "Expense records total": "Total des dossiers de dépenses",
  "{{count}} expense records": "{{count}} dossiers de dépenses",
  "GST/HST tracked only": "TPS/TVH suivie seulement",
  "Recorded trip distance": "Distance des déplacements enregistrés",
  "{{count}} trips · {{distance}} business km": "{{count}} déplacements · {{distance}} km professionnels",
  "{{count}} trips · {{distance}} business": "{{count}} déplacements · {{distance}} professionnels",
  "{{count}} trips · {{distance}}": "{{count}} déplacements · {{distance}}",
  "Review recurring expenses and missing receipts": "Réviser les dépenses récurrentes et les reçus manquants",
  "Step 2 · Resolve": "Étape 2 · Résoudre",
  "{{count}} need attention": "{{count}} à vérifier",
  "Ready to finish": "Prêt à terminer",
  "1 income record found": "1 dossier de revenu trouvé",
  "{{count}} income records found": "{{count}} dossiers de revenus trouvés",
  "1 expense found": "1 dépense trouvée",
  "{{count}} expenses found": "{{count}} dépenses trouvées",
  "1 recurring expense is still due": "1 dépense récurrente est toujours due",
  "{{count}} recurring expenses are still due": "{{count}} dépenses récurrentes sont toujours dues",
  "1 contractor bill found": "1 facture de travailleur autonome trouvée",
  "{{count}} contractor bills found": "{{count}} factures de travailleurs autonomes trouvées",
  "1 receipt is missing": "1 reçu est manquant",
  "{{count}} receipts are missing": "{{count}} reçus sont manquants",
  "1 receipt is awaiting review": "1 reçu est en attente de révision",
  "{{count}} receipts are awaiting review": "{{count}} reçus sont en attente de révision",
  "{{count}} trips · {{distance}} km": "{{count}} déplacements · {{distance}} km",
  "1 expense needs classification": "1 dépense doit être classifiée",
  "{{count}} expenses need classification": "{{count}} dépenses doivent être classifiées",
  "Step 3 · Finish": "Étape 3 · Terminer",
  "Owner review status": "État de la révision du propriétaire",
  "Add notes for your accountant": "Ajouter des notes pour votre comptable",
  "Mark month as Reviewed": "Marquer le mois comme révisé",
  "Step 4 · Inspect": "Étape 4 · Examiner",
  "Included records": "Dossiers inclus",
  "Review income, expenses, and trips before exporting.": "Révisez les revenus, les dépenses et les déplacements avant l’exportation.",
  "View receipt": "Voir le reçu",
  "Download": "Télécharger",
  "No records were found for this month.": "Aucun dossier trouvé pour ce mois.",
  "Preparing…": "Préparation…",
  "Export CSV": "Exporter en CSV",
  "Tax preparation steps": "Étapes de préparation fiscale",
  "Build records package": "Créer la trousse de dossiers",
  "Check readiness": "Vérifier l’état de préparation",
  "Use official guidance": "Consulter les directives officielles",
  "Step 1 · Prepare": "Étape 1 · Préparer",
  "Build the records package": "Créer la trousse de dossiers",
  "Choose the exact inclusive period before downloading anything for your accountant.": "Choisissez la période inclusive exacte avant de télécharger quoi que ce soit pour votre comptable.",
  "Step 2 · Record": "Étape 2 · Enregistrer",
  "Steps 3–4 · Verify": "Étapes 3–4 · Vérifier",
  "Readiness and filing guidance": "État de préparation et directives de production",
  "Resolve record gaps, then confirm the filing route with an official source or your tax professional.": "Résolvez les lacunes des dossiers, puis confirmez la méthode de production auprès d’une source officielle ou de votre fiscaliste.",
  "Income summary": "Résumé des revenus",
  "Recorded, not calculated": "Enregistré, non calculé",
  "Income records": "Dossiers de revenus",
  "Revenue recorded inside the selected month.": "Revenus enregistrés pendant le mois sélectionné.",
  "Add income when a payment is received or revenue needs to be recorded.": "Ajoutez un revenu lorsqu’un paiement est reçu ou qu’un revenu doit être enregistré.",
  "No description": "Aucune description",
  "received": "reçu",
  "pending": "en attente",
  "Invoice summary": "Résumé des factures",
  "Invoice value": "Valeur des factures",
  "Excludes voided invoices": "Exclut les factures annulées",
  "Open invoices": "Factures ouvertes",
  "Draft, scheduled or sent": "Brouillons, planifiées ou envoyées",
  "Paid invoices": "Factures payées",
  "{{count}} total invoices": "{{count}} factures au total",
  "Invoice records": "Dossiers de factures",
  "Review status before choosing an invoice action.": "Vérifiez l’état avant de choisir une action.",
  "Search invoices": "Rechercher des factures",
  "Search invoice number or client": "Rechercher un numéro de facture ou un client",
  "Filter invoices by status": "Filtrer les factures par état",
  "No invoices match this view": "Aucune facture ne correspond à cette vue",
  "Due {{date}}": "Échéance le {{date}}",
  "Invoice actions": "Actions de la facture",
  "Void invoice {{invoice}}?": "Annuler la facture {{invoice}}?",
  "Delete draft invoice {{invoice}}?": "Supprimer le brouillon de facture {{invoice}}?",
  "Production health": "Santé de la production",
  "Choose a Business before viewing production health.": "Choisissez une entreprise avant de consulter la santé de la production.",
  "Launch operations · {{business}}": "Opérations de lancement · {{business}}",
  "Monitor recent operational signals without exposing customer content, credentials or provider payloads.": "Surveillez les signaux opérationnels récents sans exposer le contenu des clients, les identifiants ni les données des fournisseurs.",
  "Refresh": "Actualiser",
  "Loading production health…": "Chargement de la santé de la production…",
  "Production health is unavailable": "La santé de la production n’est pas disponible",
  "Only Business owners and administrators can view this workspace.": "Seuls les propriétaires et les administrateurs de l’entreprise peuvent consulter cet espace.",
  "No launch blockers detected": "Aucun obstacle au lancement détecté",
  "Operational attention recommended": "Une attention opérationnelle est recommandée",
  "{{count}} failures recorded in the last 24 hours.": "{{count}} échecs enregistrés au cours des dernières 24 heures.",
  "Health checks": "Vérifications de santé",
  "Authentication": "Authentification",
  "Invoice delivery": "Livraison des factures",
  "Critical workflows": "Flux de travail essentiels",
  "Operational": "Opérationnel",
  "No paid subscription": "Aucun abonnement payant",
  "{{count}} failures · 24 hours": "{{count}} échecs · 24 heures",
  "Recent failures": "Échecs récents",
  "Sanitized operational codes from the last 30 days. No customer-entered content is stored.": "Codes opérationnels nettoyés des 30 derniers jours. Aucun contenu saisi par les clients n’est conservé.",
  "No recent failures recorded": "Aucun échec récent enregistré",
  "This view refreshes automatically every minute.": "Cette vue s’actualise automatiquement chaque minute.",
  "Checkout session failed": "Échec de la session de paiement",
  "Checkout session started": "Session de paiement démarrée",
  "Billing portal failed": "Échec du portail de facturation",
  "Billing portal opened": "Portail de facturation ouvert",
  "Invoice delivery failed": "Échec de la livraison de la facture",
  "Invoice delivery completed": "Livraison de la facture terminée",
  "Session check failed": "Échec de la vérification de session",
  "Workflow request failed": "Échec de la demande du flux de travail",
  "Operational request failed": "Échec de la demande opérationnelle",
  "auth": "authentification",
  "billing": "facturation",
  "invoice": "facture",
  "workflow": "flux de travail",
  "Active incidents": "Incidents actifs",
  "Repeated sanitized failures are grouped to reduce alert noise.": "Les échecs répétés et nettoyés sont regroupés afin de réduire le bruit des alertes.",
  "{{count}} active": "{{count}} actifs",
  "No active incidents": "Aucun incident actif",
  "Repeated failures will appear here with a sanitized operational code.": "Les échecs répétés apparaîtront ici avec un code opérationnel nettoyé.",
  "Critical": "Critique",
  "Warning": "Avertissement",
  "Acknowledged": "Pris en charge",
  "Open": "Ouvert",
  "{{count}} matching failures": "{{count}} échecs correspondants",
  "Last observed {{time}}": "Dernière observation {{time}}",
  "Acknowledge": "Prendre en charge",
  "Resolve": "Résoudre",
  "Incident could not be updated. Refresh and try again.": "L’incident n’a pas pu être mis à jour. Actualisez la page et réessayez.",
  "Create credit note": "Créer une note de crédit",
  "Paid-invoice correction": "Correction d’une facture payée",
  "Issue an immutable correction against invoice {{invoice}}. The paid invoice will not be changed.": "Émettez une correction immuable pour la facture {{invoice}}. La facture payée ne sera pas modifiée.",
  "Close credit note": "Fermer la note de crédit",
  "Available to credit": "Montant pouvant être crédité",
  "Credit amount": "Montant du crédit",
  "Enter an amount between {{minimum}} and {{maximum}}.": "Saisissez un montant entre {{minimum}} et {{maximum}}.",
  "Credit note date": "Date de la note de crédit",
  "Reason for correction": "Motif de la correction",
  "For example: returned service, pricing correction, or duplicate charge": "Par exemple : service retourné, correction de prix ou facturation en double",
  "I reviewed this correction. Once issued, the credit note cannot be edited or deleted.": "J’ai vérifié cette correction. Une fois émise, la note de crédit ne peut être ni modifiée ni supprimée.",
  "Issue credit note": "Émettre la note de crédit",
  "Issuing credit note…": "Émission de la note de crédit…",
  "Fully credited": "Entièrement créditée",
  "{{amount}} credited": "{{amount}} crédité",
  "Choose a Business first.": "Choisissez d’abord une entreprise.",
  "Keep contractor contacts, bills, payment proof and outstanding balances together for {{business}}.": "Regroupez les coordonnées des travailleurs autonomes, leurs factures, les preuves de paiement et les soldes impayés pour {{business}}.",
  "Contractor summary": "Résumé des travailleurs autonomes",
  "Active contractors": "Travailleurs autonomes actifs",
  "In this Business": "Dans cette entreprise",
  "With a balance remaining": "Avec un solde restant",
  "Outstanding": "Solde impayé",
  "All open bills · {{currency}}": "Toutes les factures ouvertes · {{currency}}",
  "Past the recorded due date": "Après la date d’échéance enregistrée",
  "Add a contractor": "Ajouter un travailleur autonome",
  "Save only the details you need to identify and pay this person or company.": "Enregistrez seulement les renseignements nécessaires pour identifier et payer cette personne ou entreprise.",
  "No email provided": "Aucune adresse courriel",
  "View bills": "Voir les factures",
  "Loading contractors…": "Chargement des travailleurs autonomes…",
  "Unable to load contractors. Refresh and try again.": "Impossible de charger les travailleurs autonomes. Actualisez et réessayez.",
  "No contractors yet": "Aucun travailleur autonome",
  "Add a contractor first, then enter their bill and record payments against it.": "Ajoutez d’abord un travailleur autonome, puis saisissez sa facture et enregistrez les paiements correspondants.",
  "Add your first contractor": "Ajouter votre premier travailleur autonome",
  "Enter contractor bills, track what is still due, and keep every payment attached to the original bill.": "Saisissez les factures des travailleurs autonomes, suivez les soldes dus et reliez chaque paiement à la facture originale.",
  "Add bill": "Ajouter une facture",
  "Add a contractor bill": "Ajouter une facture de travailleur autonome",
  "Enter the bill once. Payments will reduce its balance without creating a duplicate expense.": "Saisissez la facture une seule fois. Les paiements réduiront son solde sans créer de dépense en double.",
  "Bill filters": "Filtres des factures",
  "Search bills": "Rechercher des factures",
  "Contractor, bill number, or date": "Travailleur autonome, numéro de facture ou date",
  "Loading bills…": "Chargement des factures…",
  "Unable to load bills. Refresh and try again.": "Impossible de charger les factures. Actualisez et réessayez.",
  "Bill total": "Total de la facture",
  "Remaining": "Solde restant",
  "View bill & payments": "Voir la facture et les paiements",
  "No bills match this view": "Aucune facture ne correspond à cette vue",
  "Clear the filters to review all bills.": "Effacez les filtres pour voir toutes les factures.",
  "Add the first contractor bill when it arrives.": "Ajoutez la première facture du travailleur autonome dès sa réception.",
  "Reset search and status": "Réinitialiser la recherche et l’état",
  "Show all contractors": "Afficher tous les travailleurs autonomes",
});

const DICTIONARIES = Object.freeze({
  'en-CA': Object.freeze({}),
  'fr-CA': FRENCH_CANADIAN
});

export function normalizeInterfaceLocale(value) {
  if (SUPPORTED_LOCALES.has(value)) return value;
  if (typeof value === 'string' && value.toLowerCase().startsWith('fr')) return 'fr-CA';
  return DEFAULT_INTERFACE_LOCALE;
}

export function translateInterface(locale, key, values = {}) {
  const normalized = normalizeInterfaceLocale(locale);
  const template = DICTIONARIES[normalized]?.[key] ?? key;

  return Object.entries(values).reduce(
    (result, [name, value]) => result.replaceAll(`{{${name}}}`, String(value)),
    template
  );
}

export function readStoredInterfaceLocale(storage = globalThis?.localStorage) {
  try {
    return normalizeInterfaceLocale(storage?.getItem(INTERFACE_LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_INTERFACE_LOCALE;
  }
}

export function storeInterfaceLocale(locale, storage = globalThis?.localStorage) {
  const normalized = normalizeInterfaceLocale(locale);
  try {
    storage?.setItem(INTERFACE_LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // The in-memory preference still applies when storage is unavailable.
  }
  return normalized;
}
